import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormState } from 'react-final-form';

import {
	CheckoutOrderFragment,
	GdsService_OrderAdditionalServiceGdsServicePriceFreeze_Fragment,
	GdsServicesFragment,
	InputTraveller,
	useAddTravellersDataMutation,
	useSaveOrderServicesMutation
} from '@websky/graphql';
import { useToggleable } from '../hooks';
import { useDispatch, useSelector } from 'react-redux';
import { PassengerValue } from '../Checkout/store/passengers/reducers';
import { setPassengerValues } from '../Checkout/store/passengers/actions';
import {
	getIsPriceFreezeProductSelected,
	getIsTravellersDataFilled,
	getOrder
} from '../Checkout/store/order/selectors';
import { getParamsForSaveOrderServicesMutation, isFareLockService, isRefundOptionService } from './utils';
import { fillOrder } from '../Checkout/store/order/actions';
import { UpdateTravellers } from './types';

export const useConfirmOption = (
	order: CheckoutOrderFragment,
	onOrderLoaded: React.Dispatch<CheckoutOrderFragment>,
	serviceSelector: (service: GdsServicesFragment['services'][0]) => boolean,
	setLoading?: React.Dispatch<boolean>
) => {
	const { isOpen: isSelected, toggle, open, close } = useToggleable(false);

	const optionService = useMemo(() => order?.additionalServices.gdsServices?.services.find(serviceSelector), [order]);

	const [serviceRequest, { loading }] = useSaveOrderServicesMutation();

	useEffect(() => {
		if (setLoading) {
			setLoading(loading);
		}
	}, [loading]);

	const handleChange = useCallback(() => {
		toggle();

		if (loading) {
			return;
		}

		const travellerServices = getParamsForSaveOrderServicesMutation(
			order,
			optionService,
			isSelected ? 'remove' : 'add'
		);

		serviceRequest({
			variables: {
				params: {
					id: order.id,
					services: travellerServices
				}
			}
		}).then(data => {
			if (data?.data?.SaveOrderServices) {
				onOrderLoaded(data.data.SaveOrderServices);
			}
		});
	}, [isSelected, order, optionService, serviceRequest, loading]);

	const isAddedToOrder = order?.travellers?.some(traveller =>
		traveller?.services?.gdsServices?.services?.some(service => service?.serviceId === optionService?.id)
	);

	useEffect(() => {
		if (isAddedToOrder) {
			open();
		} else {
			close();
		}
	}, [order]);

	const isConfirmed = order?.travellers?.some(traveller =>
		traveller?.services?.gdsServices?.services?.some(
			service => service?.serviceId === optionService?.id && service?.confirmedCount
		)
	);

	return {
		handleChange,
		isSelected,
		isConfirmed,
		toggle,
		loading,
		isAddedToOrder,
		optionService,
		price: {
			amount:
				optionService?.price?.amount *
				optionService?.allowedPassengers.length *
				Array.prototype.concat.apply([], optionService?.allowedSegments).length,
			currency: optionService?.price.currency
		}
	};
};

export const useFareLock = (
	order: CheckoutOrderFragment,
	onOrderLoaded: React.Dispatch<CheckoutOrderFragment>,
	setLoading?: React.Dispatch<boolean>
) => {
	const fareLock = useConfirmOption(order, onOrderLoaded, isFareLockService, setLoading);

	return {
		...fareLock,
		optionService: fareLock.optionService as GdsService_OrderAdditionalServiceGdsServicePriceFreeze_Fragment
	};
};

export const useRefundOption = (
	order: CheckoutOrderFragment,
	onOrderLoaded: React.Dispatch<CheckoutOrderFragment>,
	setLoading?: React.Dispatch<boolean>
) => {
	return useConfirmOption(order, onOrderLoaded, isRefundOptionService, setLoading);
};

export const useUpdateDocumentsFreezeView = () => {
	const [state, setState] = useState<'review' | 'update'>('review');

	const toAddDocuments = () => setState('update');
	const toReview = () => setState('review');

	return {
		mode: state,
		toAddDocuments,
		toReview
	};
};

/**
 *
 * get formState and call SetTravellersData mutation with new values
 */

export const useUpdateTravellers = ({ setNextStep }: { setNextStep: () => void }): UpdateTravellers => {
	const dispatch = useDispatch();

	const order = useSelector(getOrder);
	const isTravellersDataFilled = useSelector(getIsTravellersDataFilled);
	const isPriceFreezeSelected = useSelector(getIsPriceFreezeProductSelected);

	const { values, valid } = useFormState();

	const [setTravellersData, { loading, data }] = useAddTravellersDataMutation();

	useEffect(() => {
		if (!valid) {
			return;
		}

		order.travellers.forEach((traveller, index) => {
			const passengerValues = traveller.values.map<PassengerValue>(value => {
				return {
					name: value.name,
					type: value.type,
					value: values['passengers'][index][value.name]
				};
			});

			dispatch(setPassengerValues(index, passengerValues));
		});
	}, [values, valid, order.travellers]);

	useEffect(() => {
		const setTravellersData = data?.SetTravellersData;

		if (isTravellersDataFilled || !setTravellersData?.isTravellersDataFilled) {
			return;
		}

		setTimeout(() => {
			setNextStep();
			dispatch(fillOrder(setTravellersData));
		});
	}, [data?.SetTravellersData, isTravellersDataFilled, setNextStep]);

	const handleSubmit = useCallback(
		async (passengersValues: InputTraveller[]) => {
			if (!isPriceFreezeSelected && isTravellersDataFilled) {
				return null;
			}

			await setTravellersData({
				variables: {
					variables: {
						orderId: order.id,
						travellersData: passengersValues
					}
				}
			});
		},
		[order.id, isPriceFreezeSelected, isTravellersDataFilled]
	);

	return {
		handleSubmit,
		loading,
		data,
		valid
	};
};
